import { cn } from "@/lib/cn";

/**
 * Table shell for this tab's five tables.
 *
 * Visually identical to the tables elsewhere in the dashboard — the same panel, the
 * same uppercase micro-header, the same hover row. Extracted because five copies of
 * that markup drift apart, not because the styling is new.
 */

interface TablePanelProps {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
  head: React.ReactNode;
  children: React.ReactNode;
  /** Rendered instead of the table body when there is nothing to show. */
  empty?: string;
  isEmpty?: boolean;
  footer?: React.ReactNode;
  className?: string;
}

export function TablePanel({
  title,
  subtitle,
  right,
  head,
  children,
  empty = "No data in this range",
  isEmpty = false,
  footer,
  className,
}: TablePanelProps) {
  return (
    <div className={cn("panel", className)}>
      <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-border">
        <div>
          <div className="text-sm font-semibold text-ink-900">{title}</div>
          {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
        {right && <div className="shrink-0">{right}</div>}
      </div>

      {isEmpty ? (
        <div className="px-5 py-8 text-center text-xs text-slate-400">{empty}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-border text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                {head}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">{children}</tbody>
          </table>
        </div>
      )}

      {footer && <div className="px-5 py-2.5 text-[10px] text-slate-400 text-right">{footer}</div>}
    </div>
  );
}

export function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th className={cn("py-2.5 px-4", align === "right" && "text-right")}>{children}</th>
  );
}

export function Td({
  children,
  align = "left",
  className,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <td
      className={cn(
        "py-2.5 px-4 text-slate-700",
        align === "right" && "text-right num-mono",
        className
      )}
    >
      {children}
    </td>
  );
}

export function Row({ children }: { children: React.ReactNode }) {
  return <tr className="hover:bg-slate-50/60 transition-colors">{children}</tr>;
}
